interface SaveFileType {
    contents: string | Blob ;
    type: string;
    filename: string;
    extension: string;
}

/**
 * Download a generated file
 * @param contents
 * @param type
 * @param filename
 * @param extension
 */
export function saveFile(
    contents: string | Blob,
    type: string,
    filename: string,
    extension: string
): void {
    const data = typeof contents === "string" ?
        new Blob([ contents ], { type }) :
        contents;

    const url = URL.createObjectURL(data);
    const element = document.createElement("a");

    element.href = url;
    element.setAttribute("download", filename + extension);
    element.click();
}

/**
 * Save multiple files in a selected directory
 * @param files
 * @param handle
 */
export async function saveMultipleFiles(files: Array<SaveFileType>, handle?: FileSystemDirectoryHandle): Promise<void> {
    // Check if feature is supported
    if(typeof window.showDirectoryPicker !== "function") {
        console.log("File System Access API not supported, falling back to anchor based download");

        for(let i = 0, l = files.length; i < l; i++) {
            saveFile(files[i].contents, files[i].type, files[i].filename, files[i].extension);
        }

        return Promise.resolve();
    }

    if(typeof handle === "undefined") {
        handle = await window.showDirectoryPicker();
    }

    // Save any file save errors for throwing later
    const errors = [];

    for(let i = 0, l = files.length; i < l; i++) {
        try {
            const file = await handle.getFileHandle(files[i].filename + files[i].extension, {
                create: true,
            });

            const writable = await file.createWritable();

            await writable.write(files[i].contents);
            await writable.close();
        } catch(e) {
            errors.push(e);
        }
    }

    if(errors.length) {
        throw errors;
    }
}

export type CSVRowValue = string | boolean | number | Record<string, unknown> | undefined | null;

/**
 * Creates a CSV file that conforms to the RFC 4180 standard
 * @param rows
 * @returns string
 */
export function createCSV(rows: Array<Array<CSVRowValue>>): string {
    const formatted = [];

    for(let i = 0, l = rows.length; i < l; i++) {
        const row = [];

        for(let j = 0, n = rows[i].length; j < n; j++) {
            let cell;

            if(typeof rows[i][j] === "undefined" || rows[i][j] === null) {
                cell = "";
            } else if(typeof rows[i][j] === "object") {
                cell = JSON.stringify(rows[i][j]);
            } else if(typeof rows[i][j] === "boolean") {
                if(rows[i][j] === true) {
                    cell = "TRUE";
                } else {
                    cell = "FALSE";
                }
            } else {
                cell = rows[i][j]?.toString() || "";
            }

            row.push(`"${cell.replace(/"/g, "\"\"")}"`);
        }

        formatted.push(row);
    }

    return formatted.join("\r\n");
}

/**
 * Compress a stream into a blob file for uploading
 */
export async function compressStreamToBlob(stream: ReadableStream): Promise<Blob> {
    // Pipe the stream through the compressor
    const compressed = stream.pipeThrough(
        new CompressionStream("gzip")
    );

    // Convert the stream into a blob
    return await new Response(compressed).blob();
}
